import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
  DialogTrigger, DialogFooter
} from '../components/ui/dialog';
import {
  Users, Plus, Loader2, Search, Mail, Phone, Building2, Briefcase, Download,
  Trash2, ChevronLeft, ChevronRight, Target, Upload, ListPlus, X, ShieldCheck, Clock, Send,
  CheckCircle2, Workflow, UserX, Tag, Eye, MousePointerClick
} from 'lucide-react';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '../components/ui/select';
import ActivityTimeline from '../components/ActivityTimeline';
import RelatedTasks from '../components/RelatedTasks';
import ContactEmailButton from '../components/ContactEmailButton';
import ReplaceContactDialog from '../components/ReplaceContactDialog';
import EmailHistory from '../components/EmailHistory';
import CustomFields from '../components/CustomFields';
import TagsLifecycle from '../components/TagsLifecycle';
import {
  RECORD_TYPES, SUPPLIER_TAGS, RecordTypeBadge,
} from '../components/CareSegment';
import ImportDialog from '../components/ImportDialog';
import AddToListDialog from '../components/AddToListDialog';
import BulkTagDialog from '../components/BulkTagDialog';
import EnrollAutomationDialog from '../components/EnrollAutomationDialog';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// GDPR marketing-consent status presentation
const MARKETING_META = {
  subscribed: { label: 'Subscribed', cls: 'border-emerald-700/50 text-emerald-700 bg-emerald-950/30' },
  confirmed: { label: 'Confirmed opt-in', cls: 'border-emerald-700/50 text-emerald-700 bg-emerald-950/30' },
  unsubscribed: { label: 'Unsubscribed', cls: 'border-rose-700/50 text-rose-700 bg-rose-950/30' },
  bounced: { label: 'Bounced', cls: 'border-amber-700/50 text-amber-700 bg-amber-950/30' },
  complained: { label: 'Complained', cls: 'border-red-700/50 text-red-700 bg-red-950/30' },
  non_marketing: { label: 'Non-marketing', cls: 'border-slate-700 text-slate-400 bg-slate-800/40' },
};
const marketingStatusOf = (c) => c.marketing_status || (c.email_opt_out ? 'unsubscribed' : 'subscribed');
const MarketingBadge = ({ status }) => {
  const meta = MARKETING_META[status] || MARKETING_META.subscribed;
  return <Badge variant="outline" className={`text-[10px] ${meta.cls}`} data-testid="marketing-status-badge">{meta.label}</Badge>;
};

// Confirmed opt-in = the contact went through the preference centre (consent_confirmed_at set).
const isConfirmed = (c) => !!c.consent_confirmed_at;
const ConfirmedIndicator = ({ contact }) => (
  isConfirmed(contact)
    ? <span className="flex items-center gap-1 text-[10px] text-emerald-400" data-testid="confirmed-indicator"><CheckCircle2 className="w-3 h-3" /> Confirmed</span>
    : <span className="text-[10px] text-slate-500" data-testid="not-confirmed-indicator">Not confirmed</span>
);

const SortTh = ({ k, label, sortBy, onSort }) => {
  const on = sortBy.key === k;
  return (
    <th className="text-left py-2 px-4">
      <button type="button" className={`inline-flex items-center gap-1 hover:text-violet-500 ${on ? 'text-violet-500 font-semibold' : ''}`} data-testid={`contacts-sort-${k}`}
        onClick={() => onSort(on && sortBy.order === 'asc' ? { key: k, order: 'desc' } : on && sortBy.order === 'desc' ? { key: '', order: 'asc' } : { key: k, order: 'asc' })}>
        {label}<span className="text-[10px]">{on ? (sortBy.order === 'asc' ? '▲' : '▼') : '↕'}</span>
      </button>
    </th>
  );
};

// Consent filter dropdown → query params for /api/contacts
const CONSENT_FILTERS = {
  all: {},
  confirmed_subscribed: { confirmed: 'yes', marketing_status: 'subscribed' },
  confirmed: { confirmed: 'yes' },
  not_confirmed: { confirmed: 'no' },
  subscribed: { marketing_status: 'subscribed' },
  non_marketing: { marketing_status: 'non_marketing' },
  unsubscribed: { marketing_status: 'unsubscribed' },
  unsubscribed_complained: { marketing_status: 'unsubscribed,complained' },
  bounced: { marketing_status: 'bounced' },
};
const CONSENT_FILTER_LABELS = {
  all: 'All consent',
  confirmed_subscribed: 'Confirmed & subscribed',
  confirmed: 'Confirmed opt-in',
  not_confirmed: 'Not confirmed',
  subscribed: 'Subscribed',
  non_marketing: 'Non-marketing',
  unsubscribed: 'Unsubscribed',
  unsubscribed_complained: 'Unsubscribed / Complained',
  bounced: 'Bounced',
};

// "Last contacted" freshness - relative label + stale (>30d, no reply) flag
const relativeTime = (iso) => {
  if (!iso) return null;
  const days = Math.floor((Date.now() - new Date(iso).getTime()) / 86400000);
  if (days < 0) return 'just now';
  if (days === 0) return 'today';
  if (days === 1) return 'yesterday';
  if (days < 30) return `${days}d ago`;
  if (days < 365) return `${Math.floor(days / 30)}mo ago`;
  return `${Math.floor(days / 365)}y ago`;
};
const LastEmailMark = ({ contact }) => {
  if (contact.last_email_clicked) {
    return <span className="inline-flex items-center gap-0.5 text-[10px] font-medium text-emerald-400 shrink-0" title="Clicked a link in this email" data-testid={`last-email-clicked-${contact.id}`}><MousePointerClick className="w-3 h-3" />Clicked</span>;
  }
  if (contact.last_email_opened) {
    return <span className="inline-flex items-center gap-0.5 text-[10px] font-medium text-sky-400 shrink-0" title="Opened this email" data-testid={`last-email-opened-${contact.id}`}><Eye className="w-3 h-3" />Opened</span>;
  }
  return null;
};
const FreshnessCell = ({ contact }) => {
  const last = contact.last_contacted_at;
  if (!last) return <span className="text-xs text-slate-600">Never contacted</span>;
  const reply = contact.last_reply_at;
  const days = Math.floor((Date.now() - new Date(last).getTime()) / 86400000);
  const replied30 = reply && (Date.now() - new Date(reply).getTime()) < 30 * 86400000;
  const isStale = days >= 30 && !replied30;
  return (
    <div className="flex items-center gap-2" data-testid="freshness-cell">
      <span className={`text-xs ${isStale ? 'text-amber-300' : 'text-slate-400'}`}>{relativeTime(last)}</span>
      {isStale && <Badge variant="outline" className="text-[10px] border-amber-700/50 text-amber-300 bg-amber-950/30" data-testid="stale-badge">Stale</Badge>}
      {reply && <span className="text-[10px] text-emerald-400/80" title={`Last reply ${relativeTime(reply)}`}>· replied</span>}
    </div>
  );
};

const Contacts = () => {
  const [contacts, setContacts] = useState([]);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [stale, setStale] = useState(false);
  const [consent, setConsent] = useState('all');
  const [recordType, setRecordType] = useState('all');
  const [supplierTag, setSupplierTag] = useState('all');
  const [sortBy, setSortBy] = useState({ key: '', order: 'asc' });
  const [hasFilter, setHasFilter] = useState('all');
  const [nudging, setNudging] = useState(false);
  const navigate = useNavigate();
  const [showCreate, setShowCreate] = useState(false);
  const [creating, setCreating] = useState(false);
  const [activeId, setActiveId] = useState(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const [selected, setSelected] = useState(new Set());
  const [showImport, setShowImport] = useState(false);
  const [showAddToList, setShowAddToList] = useState(false);
  const [showTag, setShowTag] = useState(false);
  const [showEnroll, setShowEnroll] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [backfill, setBackfill] = useState(null);
  const [newContact, setNewContact] = useState({
    full_name: '', email: '', phone: '', job_title: '',
  });

  const fetchContacts = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, page_size: 50 };
      if (search) params.search = search;
      if (stale) params.stale = true;
      if (consent && consent !== 'all') Object.assign(params, CONSENT_FILTERS[consent]);
      if (recordType !== 'all') params.record_type = recordType;
      if (supplierTag !== 'all') params.supplier_tag = supplierTag;
      if (hasFilter !== 'all') params.has = hasFilter;
      if (sortBy.key) { params.sort = sortBy.key; params.order = sortBy.order; }
      const res = await axios.get(`${API}/contacts`, { params });
      setContacts(res.data.contacts || []);
      setTotal(res.data.total || 0);
      setTotalPages(res.data.total_pages || 1);
    } catch (err) {
      toast.error('Failed to load contacts');
    } finally { setLoading(false); }
  }, [page, search, stale, consent, recordType, supplierTag, hasFilter, sortBy]);

  useEffect(() => { fetchContacts(); }, [fetchContacts]);

  const buildFilterParams = () => {
    const params = {};
    if (search) params.search = search;
    if (stale) params.stale = true;
    if (consent && consent !== 'all') Object.assign(params, CONSENT_FILTERS[consent]);
    if (recordType !== 'all') params.record_type = recordType;
    if (supplierTag !== 'all') params.supplier_tag = supplierTag;
    return params;
  };

  const exportCsv = async () => {
    setExporting(true);
    try {
      const res = await axios.get(`${API}/contacts/export/csv`, { params: buildFilterParams(), responseType: 'blob' });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url; a.download = 'contacts-export.csv'; a.click();
      URL.revokeObjectURL(url);
      toast.success('Contacts exported to CSV');
    } catch { toast.error('Export failed'); } finally { setExporting(false); }
  };

  const pollBackfill = useCallback(async () => {
    try { const { data } = await axios.get(`${API}/contacts/backfill-phones/status`); setBackfill(data); return data; }
    catch { return null; }
  }, []);
  useEffect(() => { pollBackfill(); }, [pollBackfill]);
  useEffect(() => {
    if (!backfill?.running) return undefined;
    const t = setInterval(async () => {
      const d = await pollBackfill();
      if (d && !d.running) { clearInterval(t); toast.success(`Phone backfill done - ${d.with_phone} contacts now have a number`); fetchContacts(); }
    }, 4000);
    return () => clearInterval(t);
  }, [backfill?.running, pollBackfill, fetchContacts]);

  const startBackfill = async () => {
    try {
      const { data } = await axios.post(`${API}/contacts/backfill-phones`);
      if (data.started) { toast.success('Backfilling phone numbers in the background...'); setBackfill((b) => ({ ...(b || {}), running: true })); }
      else toast.message(data.message || 'Already running');
    } catch { toast.error('Could not start backfill'); }
  };

  // Open a specific contact when navigated to with ?open=<id> (cross-linking)
  useEffect(() => {
    const openId = searchParams.get('open');
    if (openId) {
      setActiveId(openId);
      searchParams.delete('open');
      setSearchParams(searchParams, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!newContact.full_name.trim()) { toast.error('Name required'); return; }
    setCreating(true);
    try {
      const payload = {
        full_name: newContact.full_name.trim(),
        email: newContact.email || null,
        phone: newContact.phone || null,
        job_title: newContact.job_title || null,
      };
      const res = await axios.post(`${API}/contacts`, payload);
      toast.success(`Contact "${res.data.full_name}" created`);
      setShowCreate(false);
      setNewContact({ full_name: '', email: '', phone: '', job_title: '' });
      fetchContacts();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create contact');
    } finally { setCreating(false); }
  };

  const initials = (name) => (name || '?').split(' ').slice(0, 2).map(p => p[0]).join('').toUpperCase();

  const toggleSelect = (id) => setSelected(prev => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });
  const allSelected = contacts.length > 0 && contacts.every(c => selected.has(c.id));
  const toggleAll = () => setSelected(prev => {
    const next = new Set(prev);
    if (allSelected) contacts.forEach(c => next.delete(c.id));
    else contacts.forEach(c => next.add(c.id));
    return next;
  });

  const bulkSetMarketing = async (status) => {
    if (!status || selected.size === 0) return;
    try {
      const res = await axios.post(`${API}/compliance/marketing-status`, {
        contact_ids: [...selected], status,
      });
      toast.success(`Marked ${res.data.updated} contact(s) as ${MARKETING_META[status]?.label || status}`);
      setSelected(new Set());
      fetchContacts();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update consent');
    }
  };

  const nudgeContacts = async (useStale) => {
    setNudging(true);
    try {
      const body = useStale ? { stale: true } : { contact_ids: [...selected] };
      const res = await axios.post(`${API}/email-campaigns/nudge`, body);
      const skipped = res.data.skipped_suppressed ? `, ${res.data.skipped_suppressed} skipped (suppressed)` : '';
      toast.success(`Re-engagement campaign created - ${res.data.eligible} contact(s)${skipped}`);
      navigate(`/admin/campaigns/${res.data.campaign_id}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create nudge campaign');
    } finally {
      setNudging(false);
    }
  };

  return (
    <div className="space-y-6 animate-fade-in" data-testid="contacts-page">
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <h1 className="font-serif text-4xl text-white mb-2 flex items-center gap-3">
            <Users className="w-8 h-8 text-violet-400" />
            Contacts
          </h1>
          <p className="text-slate-400">Every decision-maker, linked to their company.</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" className="bg-slate-900 border-slate-700 text-slate-200" onClick={exportCsv} disabled={exporting} data-testid="export-contacts-btn">
            {exporting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Download className="w-4 h-4 mr-2" />} Export CSV
          </Button>
          <Button variant="outline" className="bg-slate-900 border-slate-700 text-slate-200" onClick={startBackfill} disabled={backfill?.running} data-testid="backfill-phones-btn"
            title={backfill ? `${backfill.with_phone} of ${backfill.total} contacts have a phone` : ''}>
            {backfill?.running ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Phone className="w-4 h-4 mr-2" />}
            {backfill?.running ? `Backfilling… (${backfill.with_phone || 0})` : 'Backfill phones'}
          </Button>
          <Button variant="outline" className="bg-slate-900 border-slate-700 text-slate-200" onClick={() => setShowImport(true)} data-testid="import-contacts-btn">
            <Upload className="w-4 h-4 mr-2" /> Import
          </Button>
          <Dialog open={showCreate} onOpenChange={setShowCreate}>
          <DialogTrigger asChild>
            <Button className="bg-violet-600 hover:bg-violet-500" data-testid="create-contact-btn">
              <Plus className="w-4 h-4 mr-2" /> New Contact
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-slate-900 border-slate-800 max-w-lg">
            <DialogHeader>
              <DialogTitle className="font-serif text-2xl text-white">New Contact</DialogTitle>
              <DialogDescription className="text-slate-400">Add a person to your CRM.</DialogDescription>
            </DialogHeader>
            <form onSubmit={handleCreate} className="space-y-3 mt-2">
              <div><Label className="text-slate-300">Full name *</Label>
                <Input value={newContact.full_name} onChange={e => setNewContact({ ...newContact, full_name: e.target.value })} className="bg-slate-950 border-slate-800" placeholder="e.g. Jane Smith" data-testid="new-contact-name" required />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div><Label className="text-slate-300">Email</Label>
                  <Input type="email" value={newContact.email} onChange={e => setNewContact({ ...newContact, email: e.target.value })} className="bg-slate-950 border-slate-800" />
                </div>
                <div><Label className="text-slate-300">Phone</Label>
                  <Input value={newContact.phone} onChange={e => setNewContact({ ...newContact, phone: e.target.value })} className="bg-slate-950 border-slate-800" />
                </div>
              </div>
              <div><Label className="text-slate-300">Job title</Label>
                <Input value={newContact.job_title} onChange={e => setNewContact({ ...newContact, job_title: e.target.value })} className="bg-slate-950 border-slate-800" placeholder="e.g. Registered Manager" />
              </div>
              <DialogFooter className="gap-2 pt-2">
                <Button type="button" variant="ghost" onClick={() => setShowCreate(false)} disabled={creating}>Cancel</Button>
                <Button type="submit" className="bg-violet-600 hover:bg-violet-500" disabled={creating} data-testid="submit-new-contact">
                  {creating ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Creating...</> : <><Plus className="w-4 h-4 mr-2" /> Create</>}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
        </div>
      </div>

      {/* Search */}
      <div className="flex gap-3 flex-wrap items-center">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
          <Input
            value={search}
            onChange={e => { setSearch(e.target.value); setPage(1); }}
            placeholder="Search contacts by name, email, title..."
            className="bg-slate-900/50 border-slate-800 pl-9"
            data-testid="contacts-search"
          />
        </div>
        <Button
          variant={stale ? 'default' : 'outline'}
          size="sm"
          onClick={() => { setStale(s => !s); setPage(1); }}
          className={stale ? 'bg-amber-600 hover:bg-amber-500 text-white' : 'bg-slate-900/50 border-slate-800 text-slate-300'}
          data-testid="stale-filter-toggle"
        >
          <Clock className="w-4 h-4 mr-1.5" /> Stale (no reply 30d)
        </Button>
        <Select value={consent} onValueChange={(v) => { setConsent(v); setPage(1); }}>
          <SelectTrigger className="h-9 w-[185px] bg-slate-900/50 border-slate-800 text-slate-300 text-sm" data-testid="consent-filter-trigger">
            <ShieldCheck className="w-4 h-4 mr-1.5 text-violet-300" />
            <SelectValue />
          </SelectTrigger>
          <SelectContent className="bg-slate-900 border-slate-800">
            {Object.keys(CONSENT_FILTER_LABELS).map((k) => (
              <SelectItem key={k} value={k} data-testid={`consent-filter-${k}`}>{CONSENT_FILTER_LABELS[k]}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={hasFilter} onValueChange={(v) => { setHasFilter(v); setPage(1); }}>
          <SelectTrigger className="h-9 w-[160px] bg-slate-900/50 border-slate-800 text-slate-300 text-sm" data-testid="contacts-has-filter">
            <SelectValue placeholder="Any details" />
          </SelectTrigger>
          <SelectContent className="bg-slate-900 border-slate-800">
            {[['all', 'Any details'], ['phone', 'Has phone'], ['no_phone', 'No phone'], ['email', 'Has email'], ['no_email', 'No email'], ['title', 'Has job title']].map(([v, l]) => <SelectItem key={v} value={v} data-testid={`contacts-has-${v}`}>{l}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={recordType} onValueChange={(v) => { setRecordType(v); setPage(1); }}>
          <SelectTrigger className="h-9 w-[150px] bg-slate-900/50 border-slate-800 text-slate-300 text-sm" data-testid="contacts-record-type-filter">
            <SelectValue placeholder="All types" />
          </SelectTrigger>
          <SelectContent className="bg-slate-900 border-slate-800">
            <SelectItem value="all">All types</SelectItem>
            {RECORD_TYPES.map(r => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={supplierTag} onValueChange={(v) => { setSupplierTag(v); setPage(1); }}>
          <SelectTrigger className="h-9 w-[160px] bg-slate-900/50 border-slate-800 text-slate-300 text-sm" data-testid="contacts-supplier-tag-filter">
            <SelectValue placeholder="All supplier tags" />
          </SelectTrigger>
          <SelectContent className="bg-slate-900 border-slate-800">
            <SelectItem value="all">All supplier tags</SelectItem>
            {SUPPLIER_TAGS.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
          </SelectContent>
        </Select>
        {stale && total > 0 && (
          <Button
            size="sm"
            onClick={() => nudgeContacts(true)}
            disabled={nudging}
            className="bg-amber-600 hover:bg-amber-500 text-white"
            data-testid="nudge-all-stale-btn"
          >
            {nudging ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Send className="w-4 h-4 mr-1.5" />} Nudge all {total.toLocaleString()}
          </Button>
        )}
        <span className="text-sm text-slate-500" data-testid="contacts-total">{total.toLocaleString()} {stale ? 'stale ' : ''}contacts</span>
      </div>

      {/* Bulk selection toolbar */}
      {selected.size > 0 && (
        <div className="flex items-center gap-2 flex-wrap bg-violet-950/30 border border-violet-800/40 rounded-lg px-4 py-2.5" data-testid="contacts-bulk-toolbar">
          <span className="text-sm text-violet-200">{selected.size} selected</span>
          <Button size="sm" className="bg-violet-600 hover:bg-violet-500" onClick={() => setShowAddToList(true)} data-testid="bulk-add-to-list-btn">
            <ListPlus className="w-4 h-4 mr-1.5" /> Add to list
          </Button>
          <Button size="sm" className="bg-fuchsia-600 hover:bg-fuchsia-500 text-white" onClick={() => setShowTag(true)} data-testid="bulk-tag-btn">
            <Tag className="w-4 h-4 mr-1.5" /> Tag
          </Button>
          <Button size="sm" className="bg-violet-700 hover:bg-violet-600 text-white" onClick={() => setShowEnroll(true)} data-testid="bulk-enroll-automation-btn">
            <Workflow className="w-4 h-4 mr-1.5" /> Enroll
          </Button>
          <Button size="sm" className="bg-amber-600 hover:bg-amber-500 text-white" onClick={() => nudgeContacts(false)} disabled={nudging} data-testid="bulk-nudge-btn">
            {nudging ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Send className="w-4 h-4 mr-1.5" />} Nudge
          </Button>
          <Select onValueChange={bulkSetMarketing}>
            <SelectTrigger className="h-8 w-[180px] bg-slate-900 border-slate-700 text-slate-200 text-sm" data-testid="bulk-marketing-status-trigger">
              <ShieldCheck className="w-4 h-4 mr-1.5 text-violet-300" />
              <SelectValue placeholder="Set consent…" />
            </SelectTrigger>
            <SelectContent className="bg-slate-900 border-slate-800">
              <SelectItem value="subscribed" data-testid="bulk-status-subscribed">Subscribed</SelectItem>
              <SelectItem value="confirmed" data-testid="bulk-status-confirmed">Confirmed (opt-in)</SelectItem>
              <SelectItem value="unsubscribed" data-testid="bulk-status-unsubscribed">Unsubscribed</SelectItem>
              <SelectItem value="non_marketing" data-testid="bulk-status-non-marketing">Non-marketing</SelectItem>
              <SelectItem value="bounced" data-testid="bulk-status-bounced">Bounced</SelectItem>
            </SelectContent>
          </Select>
          <Button size="sm" variant="ghost" className="text-slate-400" onClick={() => setSelected(new Set())} data-testid="clear-selection-btn">
            <X className="w-4 h-4 mr-1" /> Clear
          </Button>
        </div>
      )}

      {/* Table */}
      {loading ? (
        <div className="flex justify-center py-20" data-testid="contacts-loading"><Loader2 className="w-8 h-8 animate-spin text-slate-500" /></div>
      ) : contacts.length === 0 ? (
        <Card className="bg-slate-900/50 border-slate-800">
          <CardContent className="text-center py-16 text-slate-500">
            No contacts yet. Migrate your leads from the Companies page, or click "New Contact".
          </CardContent>
        </Card>
      ) : (
        <>
        {/* Desktop / tablet table */}
        <Card className="bg-slate-900/50 border-slate-800 hidden md:block">
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-slate-700 text-xs text-slate-500 uppercase tracking-wider">
                    <th className="py-2 px-4 w-10"><input type="checkbox" checked={allSelected} onChange={toggleAll} className="w-4 h-4 accent-violet-600 cursor-pointer" data-testid="select-all-contacts" /></th>
                    <SortTh k="name" label="Name" sortBy={sortBy} onSort={(v) => { setSortBy(v); setPage(1); }} />
                    <th className="text-left py-2 px-4">Company</th>
                    <SortTh k="email" label="Email" sortBy={sortBy} onSort={(v) => { setSortBy(v); setPage(1); }} />
                    <SortTh k="phone" label="Phone" sortBy={sortBy} onSort={(v) => { setSortBy(v); setPage(1); }} />
                    <SortTh k="title" label="Title" sortBy={sortBy} onSort={(v) => { setSortBy(v); setPage(1); }} />
                    <SortTh k="last_contacted" label="Last contacted" sortBy={sortBy} onSort={(v) => { setSortBy(v); setPage(1); }} />
                    <th className="text-left py-2 px-4">Last email</th>
                    <th className="text-left py-2 px-4">Consent</th>
                  </tr>
                </thead>
                <tbody>
                  {contacts.map(c => (
                    <tr key={c.id} className={`border-b border-slate-800 hover:bg-slate-800/30 cursor-pointer ${selected.has(c.id) ? 'bg-violet-950/20' : ''}`} data-testid={`contact-row-${c.id}`}>
                      <td className="py-3 px-4" onClick={(e) => e.stopPropagation()}>
                        <input type="checkbox" checked={selected.has(c.id)} onChange={() => toggleSelect(c.id)} className="w-4 h-4 accent-violet-600 cursor-pointer" data-testid={`select-contact-${c.id}`} />
                      </td>
                      <td className="py-3 px-4" onClick={() => setActiveId(c.id)}>
                        <div className="flex items-center gap-2.5">
                          <span className="w-7 h-7 rounded-full bg-violet-600/20 text-violet-300 text-[10px] font-semibold flex items-center justify-center shrink-0">{initials(c.full_name)}</span>
                          <span className="text-sm text-white">{c.full_name}</span>
                        </div>
                      </td>
                      <td className="py-3 px-4 text-sm text-slate-300" onClick={() => setActiveId(c.id)}>{c.company_name || '-'}</td>
                      <td className="py-3 px-4 text-xs text-slate-400" onClick={(e) => e.stopPropagation()}>{c.email ? <a href={`mailto:${c.email}`} className="hover:text-emerald-400" data-testid={`contact-mail-${c.id}`}>{c.email}</a> : '-'}</td>
                      <td className="py-3 px-4 text-xs text-slate-400" onClick={(e) => e.stopPropagation()}>{c.phone ? <a href={`tel:${c.phone}`} className="hover:text-emerald-400" data-testid={`contact-tel-${c.id}`}>{c.phone}</a> : '-'}</td>
                      <td className="py-3 px-4 text-xs text-slate-400" onClick={() => setActiveId(c.id)}>{c.job_title || '-'}</td>
                      <td className="py-3 px-4" onClick={() => setActiveId(c.id)}><FreshnessCell contact={c} /></td>
                      <td className="py-3 px-4 text-xs text-slate-400 max-w-[220px]" onClick={() => setActiveId(c.id)}>
                        <span className="flex items-center gap-1.5 min-w-0">
                          <span className="truncate" title={c.last_email_subject || ''} data-testid={`contact-last-email-${c.id}`}>{c.last_email_subject || '-'}</span>
                          {c.last_email_subject && <LastEmailMark contact={c} />}
                        </span>
                      </td>
                      <td className="py-3 px-4" onClick={() => setActiveId(c.id)}>
                        <div className="flex flex-col gap-1 items-start">
                          <MarketingBadge status={marketingStatusOf(c)} />
                          <ConfirmedIndicator contact={c} />
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>

        {/* Mobile stacked cards */}
        <div className="md:hidden space-y-3" data-testid="contacts-mobile-list">
          {contacts.map(c => (
            <div key={c.id} className={`bg-slate-900/50 border border-slate-800 rounded-xl p-3 flex gap-3 ${selected.has(c.id) ? 'ring-2 ring-violet-500' : ''}`} data-testid={`contact-card-${c.id}`}>
              <label className="min-h-[44px] flex items-start pt-1 pr-1" onClick={(e) => e.stopPropagation()}>
                <input type="checkbox" checked={selected.has(c.id)} onChange={() => toggleSelect(c.id)} className="w-5 h-5 accent-violet-600" data-testid={`select-contact-m-${c.id}`} />
              </label>
              <div className="flex-1 min-w-0" onClick={() => setActiveId(c.id)}>
                <div className="flex items-center gap-2">
                  <span className="w-8 h-8 rounded-full bg-violet-600/20 text-violet-300 text-xs font-semibold flex items-center justify-center shrink-0">{initials(c.full_name)}</span>
                  <span className="text-base font-medium text-white truncate">{c.full_name}</span>
                </div>
                <div className="mt-2 space-y-0.5 text-sm">
                  {c.company_name && <div className="text-slate-300 truncate">{c.company_name}</div>}
                  {c.email && <div className="text-slate-400 truncate">{c.email}</div>}
                  {c.phone && <div className="text-slate-400 truncate">{c.phone}</div>}
                  {c.job_title && <div className="text-slate-400 truncate">{c.job_title}</div>}
                </div>
                <div className="mt-2 flex items-center gap-2 flex-wrap">
                  <MarketingBadge status={marketingStatusOf(c)} />
                  <ConfirmedIndicator contact={c} />
                  <FreshnessCell contact={c} />
                </div>
                {c.last_email_subject && <p className="flex items-center gap-1.5 text-[11px] text-slate-500 mt-1 min-w-0" data-testid={`contact-last-email-mobile-${c.id}`}><span className="truncate">Last email: {c.last_email_subject}</span><LastEmailMark contact={c} /></p>}
              </div>
            </div>
          ))}
        </div>
        </>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-center gap-3">
          <Button variant="outline" size="sm" className="bg-slate-900 border-slate-700" disabled={page <= 1} onClick={() => setPage(p => p - 1)} data-testid="contacts-prev">
            <ChevronLeft className="w-4 h-4" /> Prev
          </Button>
          <span className="text-sm text-slate-400">Page {page} of {totalPages}</span>
          <Button variant="outline" size="sm" className="bg-slate-900 border-slate-700" disabled={page >= totalPages} onClick={() => setPage(p => p + 1)} data-testid="contacts-next">
            Next <ChevronRight className="w-4 h-4" />
          </Button>
        </div>
      )}

      <ContactDetailDialog contactId={activeId} onClose={() => setActiveId(null)} onChanged={fetchContacts} />

      <ImportDialog objectType="contact" open={showImport} onOpenChange={setShowImport} onDone={fetchContacts} />
      <AddToListDialog objectType="contact" ids={[...selected]} open={showAddToList} onOpenChange={setShowAddToList} onDone={() => setSelected(new Set())} />
      <BulkTagDialog ids={[...selected]} open={showTag} onOpenChange={setShowTag} onDone={() => { setSelected(new Set()); fetchContacts(); }} />
      <EnrollAutomationDialog ids={[...selected]} open={showEnroll} onOpenChange={setShowEnroll} onDone={() => setSelected(new Set())} />
    </div>
  );
};

const ContactDetailDialog = ({ contactId, onClose, onChanged }) => {
  const navigate = useNavigate();
  const [contact, setContact] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showReplace, setShowReplace] = useState(false);

  const load = useCallback(() => {
    if (!contactId) { setContact(null); return; }
    setLoading(true);
    axios.get(`${API}/contacts/${contactId}`)
      .then(res => setContact(res.data))
      .catch(() => toast.error('Failed to load contact'))
      .finally(() => setLoading(false));
  }, [contactId]);

  useEffect(() => { load(); }, [load]);

  const reload = () => { load(); if (onChanged) onChanged(); };

  const handleDelete = async () => {
    if (!window.confirm(`Delete "${contact.full_name}"?`)) return;
    try {
      await axios.delete(`${API}/contacts/${contact.id}`);
      toast.success('Contact deleted');
      onClose();
      onChanged();
    } catch { toast.error('Failed to delete'); }
  };

  return (
    <Dialog open={!!contactId} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-slate-900 border-slate-800 max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="contact-detail-dialog">
        {loading || !contact ? (
          <div className="flex justify-center py-16"><DialogTitle className="sr-only">Loading contact</DialogTitle><Loader2 className="w-6 h-6 animate-spin text-slate-500" /></div>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle className="font-serif text-2xl text-white flex items-center gap-2">
                {contact.full_name}
                {contact.left_company && (
                  <Badge variant="outline" className="text-[10px] bg-amber-100 text-amber-700 border-amber-200" data-testid="left-company-badge">
                    <UserX className="w-3 h-3 mr-1" /> Left company
                  </Badge>
                )}
              </DialogTitle>
              <DialogDescription className="text-slate-400 flex flex-wrap items-center gap-x-3 gap-y-1">
                {contact.job_title && <span className="flex items-center gap-1"><Briefcase className="w-3 h-3" /> {contact.job_title}</span>}
                {contact.company?.name && (
                  <button
                    type="button"
                    onClick={() => { onClose(); navigate(`/admin/companies?open=${contact.company.id || contact.company_id}`); }}
                    className="flex items-center gap-1 text-violet-400 hover:text-violet-300 hover:underline"
                    data-testid="contact-company-link"
                  >
                    <Building2 className="w-3 h-3" /> {contact.company.name}
                  </button>
                )}
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-5 mt-2">
              <div className="flex flex-wrap gap-3">
                {contact.email && <ContactEmailButton contact={contact} />}
                {contact.email && (
                  <a href={`mailto:${contact.email}`} className="flex items-center gap-2 text-sm text-violet-400 hover:text-violet-300 bg-slate-950 border border-slate-800 rounded-md px-3 py-2">
                    <Mail className="w-4 h-4" /> {contact.email}
                  </a>
                )}
                {contact.phone && (
                  <span className="flex items-center gap-2 text-sm text-slate-300 bg-slate-950 border border-slate-800 rounded-md px-3 py-2">
                    <Phone className="w-4 h-4" /> {contact.phone}
                  </span>
                )}
              </div>

              {/* Last contacted (freshness) */}
              <div className="bg-slate-950 border border-slate-800 rounded-md px-3 py-2.5 flex items-center justify-between gap-3" data-testid="contact-freshness-row">
                <div className="flex items-center gap-2">
                  <Clock className="w-4 h-4 text-amber-300" />
                  <span className="text-sm text-slate-300">Last contacted</span>
                </div>
                <div className="flex flex-col items-end min-w-0 max-w-[60%]">
                  <FreshnessCell contact={contact} />
                  {contact.last_email_subject && <span className="flex items-center gap-1.5 text-[11px] text-slate-500 max-w-[260px] min-w-0" data-testid="contact-last-email-subject"><span className="truncate" title={contact.last_email_subject}>{contact.last_email_subject}</span><LastEmailMark contact={contact} /></span>}
                </div>
              </div>

              {/* Marketing consent (GDPR) */}
              <div className="bg-slate-950 border border-slate-800 rounded-md px-3 py-2.5 flex items-center justify-between gap-3" data-testid="contact-consent-row">
                <div className="flex items-center gap-2 flex-wrap">
                  <ShieldCheck className="w-4 h-4 text-violet-300" />
                  <span className="text-sm text-slate-300">Marketing consent</span>
                  <MarketingBadge status={marketingStatusOf(contact)} />
                  {isConfirmed(contact)
                    ? <span className="flex items-center gap-1 text-[11px] text-emerald-400" data-testid="detail-confirmed">
                        <CheckCircle2 className="w-3.5 h-3.5" /> Confirmed opt-in{contact.consent_confirmed_at ? ` · ${new Date(contact.consent_confirmed_at).toLocaleDateString()}` : ''}
                      </span>
                    : <span className="text-[11px] text-slate-500" data-testid="detail-not-confirmed">Not confirmed (assumed)</span>}
                </div>
                <Select
                  value={marketingStatusOf(contact)}
                  onValueChange={async (status) => {
                    try {
                      await axios.post(`${API}/compliance/marketing-status`, { contact_ids: [contact.id], status });
                      toast.success(`Marked as ${MARKETING_META[status]?.label || status}`);
                      reload();
                    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to update'); }
                  }}
                >
                  <SelectTrigger className="h-8 w-[170px] bg-slate-900 border-slate-700 text-slate-200 text-sm" data-testid="contact-consent-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-slate-900 border-slate-800">
                    <SelectItem value="subscribed">Subscribed</SelectItem>
                    <SelectItem value="confirmed" data-testid="detail-status-confirmed">Confirmed (opt-in)</SelectItem>
                    <SelectItem value="unsubscribed">Unsubscribed</SelectItem>
                    <SelectItem value="non_marketing">Non-marketing</SelectItem>
                    <SelectItem value="bounced">Bounced</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Deals */}
              <div>
                <Label className="text-slate-300 mb-2 block flex items-center gap-2"><Target className="w-4 h-4" /> Deals ({contact.deals?.length || 0})</Label>
                {(!contact.deals || contact.deals.length === 0) ? (
                  <p className="text-xs text-slate-600 italic">No deals yet.</p>
                ) : (
                  <div className="space-y-2">
                    {contact.deals.map(d => (
                      <div key={d.id} className="bg-slate-950 border border-slate-800 rounded-md p-3 flex items-center justify-between">
                        <span className="text-sm text-white">{d.title}</span>
                        <Badge variant="outline" className="text-[10px] border-slate-700 text-slate-400 capitalize">{(d.stage || '').replace(/_/g, ' ')}</Badge>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <EmailHistory emails={contact.emails} />
              <div className="flex items-center gap-3" data-testid="influence-level-row">
                <Label className="text-slate-300 text-sm whitespace-nowrap">Influence</Label>
                <Select
                  value={contact.influence_level || 'none'}
                  onValueChange={async (v) => {
                    try {
                      await axios.patch(`${API}/contacts/${contact.id}`, { influence_level: v === 'none' ? null : v });
                      toast.success('Influence updated');
                      reload();
                    } catch { toast.error('Failed to update'); }
                  }}
                >
                  <SelectTrigger className="bg-slate-950 border-slate-800 w-[200px]" data-testid="influence-level-select"><SelectValue placeholder="-" /></SelectTrigger>
                  <SelectContent className="bg-slate-900 border-slate-800">
                    <SelectItem value="none">- not set -</SelectItem>
                    <SelectItem value="decision_maker">Decision maker</SelectItem>
                    <SelectItem value="influencer">Influencer</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <TagsLifecycle objectType="contact" recordId={contact.id} tags={contact.tags} lifecycle={contact.lifecycle_stage} onSaved={reload} />
              <CustomFields objectType="contact" recordId={contact.id} value={contact.custom_fields} onSaved={reload} />
              <ActivityTimeline parentType="contact" parentId={contact.id} />
              <RelatedTasks relatedType="contact" relatedId={contact.id} relatedName={contact.full_name || contact.email} />
            </div>

            <DialogFooter className="gap-2 pt-2">
              <Button variant="ghost" onClick={handleDelete} className="text-rose-400 hover:bg-rose-900/20" data-testid="delete-contact-btn">
                <Trash2 className="w-4 h-4 mr-1" /> Delete
              </Button>
              {!contact.left_company && (
                <Button variant="outline" onClick={() => setShowReplace(true)} className="bg-amber-50 border-amber-200 text-amber-700 hover:bg-amber-100" data-testid="mark-left-replace-btn">
                  <UserX className="w-4 h-4 mr-1" /> Mark as left / replace
                </Button>
              )}
              <Button variant="outline" onClick={onClose} className="bg-slate-900 border-slate-700">Close</Button>
            </DialogFooter>

            <ReplaceContactDialog
              contact={contact}
              open={showReplace}
              onOpenChange={setShowReplace}
              onDone={(newContact) => {
                onChanged();
                onClose();
                if (newContact?.id) navigate(`/admin/contacts?open=${newContact.id}`);
              }}
            />
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default Contacts;
